import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
  ALL_RECORDS_CQL,
  CourseReserveDetailsData,
  CourseReserveSearchState,
  CourseReservesItemDto,
  ReserveListingsDto,
  SearchInstanceDto,
  SORT_FIELD,
} from '@app/components';
import { usePaging } from '@app/hooks';
import { makeRequest } from '@app/utils';
import { RECORDS_PER_PAGE, SORT_DIRECTION } from '@app/constants';

interface ReserveListings {
  reserves: ReserveListingsDto[];
  totalRecords: number;
}

interface CourseReserveDetailsDto {
  courses: CourseReservesItemDto[];
}

interface ReserveListingsTransformed {
  reserves: CourseReserveDetailsData[];
  totalRecords: number;
}

interface CourseReserveDetails {
  courseQuery: UseQueryResult<CourseReserveDetailsDto>;
  courseListingsQuery: UseQueryResult<ReserveListingsTransformed>;
  courseReserve: CourseReservesItemDto;
  totalRecords: number;
  pages: SearchInstanceDto[][];
  currentPage?: CourseReserveDetailsData[];
}

const sortFieldMap = {
  [SORT_FIELD.title]: 'copiedItem.title',
  [SORT_FIELD.author]: 'copiedItem.contributors',
  [SORT_FIELD.publicationYear]: 'copiedItem.publicationYear',
};

const formatCourseListingsData = (
  data: ReserveListingsDto[] = []
): CourseReserveDetailsData[] =>
  data.map(
    ({
      copiedItem: {
        instanceId,
        title,
        contributors,
        publication,
        sourceTypes,
        isbns,
      },
    }) => ({
      id: instanceId,
      title,
      publication,
      contributors,
      sourceTypes,
      isbns,
    })
  );

export const useCourseReserveDetailsFetch = (
  state: CourseReserveSearchState
): CourseReserveDetails => {
  const { id } = useParams();
  const [courseReserve, setCourseReserve] = useState<CourseReservesItemDto>(
    null!
  );
  const [courseListingId, setCourseListingId] = useState('');
  const [pages, setPages] = useState<SearchInstanceDto[][]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const courseReserveDetailsQuery = useQuery(['courseReserveDetails', id], () =>
    makeRequest<CourseReserveDetailsDto>({
      url: '/opac-courses/courses',
      params: {
        query: `id==${id}`,
      },
    })
  );

  useEffect(() => {
    if (courseReserveDetailsQuery.data) {
      setCourseReserve(courseReserveDetailsQuery.data.courses[0]);
      setCourseListingId(
        courseReserveDetailsQuery.data.courses[0].courseListingId
      );
    }
  }, [courseReserveDetailsQuery.data]);

  const courseListingsQuery = useQuery<
    ReserveListings,
    unknown,
    ReserveListingsTransformed
  >(
    ['courseListings', courseListingId, state],
    () =>
      makeRequest<ReserveListings>({
        url: `/opac-courses/courselistings/${courseListingId}/reserves`,
        params: {
          query: `${ALL_RECORDS_CQL} sortBy ${
            sortFieldMap[state.sortField ?? SORT_FIELD.title]
          }/sort.${state.sortDirection ?? SORT_DIRECTION.asc}`,
          limit: RECORDS_PER_PAGE,
          ...(state.pageNumber > 1 && {
            offset: RECORDS_PER_PAGE * (state.pageNumber - 1),
          }),
        },
      }),
    {
      enabled: Boolean(courseListingId),
      cacheTime: 0,
      select: data => ({
        ...data,
        reserves: formatCourseListingsData(data.reserves),
      }),
    }
  );

  useEffect(() => {
    if (courseListingsQuery.data?.totalRecords !== undefined) {
      setTotalRecords(courseListingsQuery.data.totalRecords);
    }
  }, [courseListingsQuery.data?.totalRecords]);

  usePaging<CourseReserveDetailsData>({
    areRecordsFetched: courseListingsQuery.isSuccess,
    records: courseListingsQuery.data?.reserves,
    isNewPageRequest: state.pageNumber !== 1,
    pageNumber: state.pageNumber,
    setPages,
  });

  return {
    courseReserve,
    courseQuery: courseReserveDetailsQuery,
    courseListingsQuery,
    pages,
    totalRecords,
    currentPage: courseListingsQuery.data?.reserves,
  };
};
